import { useEffect, useCallback, useRef, useState } from 'react';
import type { UseWebSocketReturn } from './useWebSocket';
import type { UseMessagesReturn } from './useMessages';
import type { UseSessionReturn } from './useSession';
import { i18n } from '../i18n';
import { ClientEventType, ServerEventType } from '../types/events';
import type {
  SessionReadyEvent,
  SessionRestoredEvent,
  ChatSettingsEvent,
  StreamingChunkEvent,
  StreamCompleteEvent,
  NewMessageEvent,
  ErrorEvent,
  ChatSettingsData,
  LanguageUpdateEvent,
  LocationScope,
  Message,
  PreChatForm,
  WhatsAppHandoff,
  WhatsAppHandoffEvent,
} from '../types/events';

/**
 * Error codes returned when a session can no longer be restored (expired,
 * evicted, or belonging to a different router). These are expected on a
 * returning visitor and should silently start a fresh session rather than
 * surface a user-facing error.
 */
const RESTORE_FAILURE_CODES = new Set(['SESSION_NOT_FOUND', 'SESSION_ROUTER_MISMATCH']);

/**
 * tool_call_id for the location question asked when the widget opens (CAIV-677).
 *
 * Minted client-side rather than by a real tool call, so a visitor who picks a club and leaves
 * without typing costs nothing: no conversation, no metering row, no analytics candidate. It is
 * a fixed string, not a generated one, because the bottom sheet persists dismissal per
 * tool_call_id: a fresh id on every open would re-pop a sheet the visitor closed.
 */
export const LOCATION_SCOPE_TOOL_CALL_ID = 'local_location_scope';

/**
 * tool_call_id for the contact form asked before the chat starts (CAIV-362).
 *
 * Fixed, not generated, for the same reason the location question's is: the bottom sheet
 * persists dismissal per tool_call_id, so a fresh id on every open would re-pop a sheet the
 * visitor already dealt with.
 */
export const PRECHAT_FORM_TOOL_CALL_ID = 'local_prechat_form';

/**
 * Metadata for the bubble that carries the pre-chat form, or undefined when the club does not
 * ask for one.
 *
 * Rendered through the existing `user_form` tool UI rather than a new component: it is the same
 * form `collect_user_information` shows, asked earlier, and a second implementation would drift
 * from the first on validation, locales and layout.
 */
export function prechatCardMetadata(
  form: PreChatForm | null | undefined,
  answered: boolean
): Record<string, unknown> | undefined {
  if (!form) return undefined;
  const toolUi = {
    tool_call_id: PRECHAT_FORM_TOOL_CALL_ID,
    tool_name: 'prechat_form',
    ui_type: 'user_form',
    ui_config: {
      form_schema: {
        type: 'object',
        title: form.title,
        properties: form.fields,
        required: form.required,
      },
      // Without this the renderer falls back to its own default field list and asks for a
      // surname and a name prefix the server never requested. The gate asks for as little as
      // it can get away with, so the list has to come from the server.
      fields: Object.keys(form.fields),
      required_fields: form.required,
      // Personal details captured before the conversation starts, so the visitor is told what
      // they are for before handing them over, not afterwards in a policy nobody opens.
      consent: i18n.t('prechat.consent', {
        ns: 'widget',
        defaultValue:
          'We use your details only to answer your question and to contact you about it.',
      }),
    },
  };
  if (!answered) return { tool_ui: toolUi };
  // Locked rather than removed: the answer stays visible in the transcript where it was given,
  // it just stops being a way back in. The summary is what makes it read as answered at all;
  // without one the card still says "tap to open" and invites a visitor to fill in details the
  // club already has.
  return {
    tool_ui: toolUi,
    tool_ui_locked: true,
    // Its own key rather than the generic form's: this confirms the club now has a way to
    // reach them, which is a different thing from a form having been submitted.
    tool_ui_summary: i18n.t('prechat.submitted', {
      ns: 'widget',
      defaultValue: 'Contact details received',
    }),
  };
}

/** The contact question, used as the greeting when the club configured none. */
function askContactPrompt(): string {
  return i18n.t('prechat.ask', {
    ns: 'widget',
    defaultValue: 'Before we start, how can we reach you?',
  });
}

/** The question, used as the greeting when the club configured none. */
function askLocationPrompt(): string {
  return i18n.t('locationScope.ask', {
    ns: 'widget',
    defaultValue: 'Which club is your question about?',
  });
}

/** The shortlist is only worth offering when there is more than one club on it. */
function hasShortlist(scope?: LocationScope | null): boolean {
  return scope?.mode === 'ask' && (scope.locations?.length ?? 0) > 1;
}

/**
 * Metadata for the bubble that carries the location question, or undefined when there is nothing
 * to carry.
 *
 * Unanswered it is the mandatory picker. Answered it is the `📍 <club>` confirmation, which the
 * visitor can reopen to correct a mis-tap. `tool_ui_locked` marks the club as settled: the
 * confirmation still says which club is answering, it just stops being a way in. The server
 * decides that (`can_change`), because it is the one that knows a prospect now exists.
 */
function locationCardMetadata(
  scope: LocationScope | null | undefined,
  pickedLocationId: number | null
): Record<string, unknown> | undefined {
  if (!hasShortlist(scope)) return undefined;
  const toolUi = {
    tool_call_id: LOCATION_SCOPE_TOOL_CALL_ID,
    tool_name: 'select_location',
    ui_type: 'location_picker',
    ui_config: { all_locations: scope?.locations ?? [] },
  };
  if (pickedLocationId === null) return { tool_ui: toolUi };
  const picked = scope?.locations?.find((loc) => String(loc.id) === String(pickedLocationId));
  if (!picked) return undefined;
  // Carried on the message because the club lives on the conversation, not in this tab: a reload
  // in a fresh tab has no sheet state to read the confirmation from.
  const card: Record<string, unknown> = {
    tool_ui: toolUi,
    tool_ui_summary: `📍 ${picked.name}`,
  };
  if (scope?.can_change === false) card.tool_ui_locked = true;
  return card;
}

/**
 * Message history item from server (snake_case).
 */
interface ServerMessageHistory {
  id: string;
  content: string;
  sender_type: 'user' | 'assistant' | 'system';
  created_at: string;
  metadata?: Record<string, unknown>;
}

/**
 * Extended session restored event with message history.
 */
interface SessionRestoredWithHistory extends SessionRestoredEvent {
  data: SessionRestoredEvent['data'] & {
    messages?: ServerMessageHistory[];
  };
}

/**
 * Options for the useChatEventHandlers hook.
 */
export interface UseChatEventHandlersOptions {
  /** WebSocket hook instance */
  websocket: UseWebSocketReturn;
  /** Messages hook instance */
  messages: UseMessagesReturn;
  /** Session hook instance */
  session: UseSessionReturn;
  /** Callback when chat settings are received */
  onChatSettings?: (settings: ChatSettingsData) => void;
  /** Callback when session language changes */
  onLanguageUpdate?: (language: string) => void;
  /** Callback when an error occurs */
  onError?: (message: string, code?: string) => void;
  /** Callback when the server pushes the WhatsApp handoff banner (CAIV-1063) */
  onWhatsAppHandoff?: (handoff: WhatsAppHandoff, raise: boolean) => void;
}

/**
 * Return type for the useChatEventHandlers hook.
 */
export interface UseChatEventHandlersReturn {
  /** Chat settings received from the server (via WebSocket) */
  chatSettings: ChatSettingsData | null;
  /** Last error message */
  lastError: string | null;
  /** Clear the last error */
  clearError: () => void;
}

/**
 * Hook that wires up WebSocket event handlers to manage chat state.
 *
 * Handles all server events by coordinating between the websocket,
 * messages, and session hooks to maintain chat state.
 *
 * @param options - Hook dependencies and callbacks
 * @returns Chat settings and error state
 */
export function useChatEventHandlers(
  options: UseChatEventHandlersOptions
): UseChatEventHandlersReturn {
  const {
    websocket,
    messages,
    session,
    onChatSettings,
    onLanguageUpdate,
    onError,
    onWhatsAppHandoff,
  } = options;

  const [chatSettings, setChatSettings] = useState<ChatSettingsData | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  // chat_settings lands before session_restored on every connect, so the shortlist and the
  // greeting are held here for the restore handler to rebuild the location bubble with.
  const locationScopeRef = useRef<LocationScope | null>(null);
  const prechatFormRef = useRef<PreChatForm | null>(null);
  const salesStarterRef = useRef<string>('');
  // Always the latest transcript, without re-subscribing every handler on each streamed chunk.
  // The location bubble is rewritten in place from these, so a stale copy would drop whatever
  // arrived since it was captured.
  const messagesRef = useRef<Message[]>([]);
  const { on: onWebSocketEvent } = websocket;
  const {
    addBotMessage,
    addSystemMessage,
    startStreamingMessage,
    appendStreamingChunk,
    finalizeStreamingMessage,
    setMessageHistory,
    setAssistantTyping,
    messages: currentMessages,
  } = messages;
  const { saveSession, getLocationId, saveLocationId, getContactDetails } = session;
  messagesRef.current = currentMessages;

  /**
   * Clear the last error.
   */
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  /**
   * Convert server message history to client Message format.
   */
  const convertServerMessages = useCallback((
    serverMessages: ServerMessageHistory[]
  ): Message[] => {
    return serverMessages.map((msg) => ({
      id: msg.id,
      content: msg.content,
      senderType: msg.sender_type,
      timestamp: new Date(msg.created_at),
      isStreaming: false,
      metadata: msg.metadata,
    }));
  }, []);

  // Register event handlers
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Handle session_ready - store session_id in localStorage
    unsubscribers.push(
      onWebSocketEvent<SessionReadyEvent>(ServerEventType.SESSION_READY, (event) => {
        const { session_id } = event.data;
        saveSession(session_id);
      })
    );

    // Handle session_restored - populate message history
    unsubscribers.push(
      onWebSocketEvent<SessionRestoredWithHistory>(ServerEventType.SESSION_RESTORED, (event) => {
        const { session_id, messages: serverMessages, location_id } = event.data;

        // Update session ID if it changed
        saveSession(session_id);

        // The conversation is already scoped, so the question has been answered even if this
        // browser has forgotten it (another device, cleared storage, a pick made mid-flow).
        if (typeof location_id === 'number') {
          saveLocationId(location_id);
        }

        // Restore message history if provided
        if (serverMessages && serverMessages.length > 0) {
          const convertedMessages = convertServerMessages(serverMessages);
          // The location question is the widget's own bubble, never a server message, so a
          // restore would drop it and with it the visitor's only way back to the picker. It
          // belongs at the head of the transcript, where they answered it: keep the one already
          // on screen (chat_settings arrives before this event) or rebuild it from the club the
          // server just confirmed.
          // Rebuilt from the club the *server* confirms rather than from the bubble already on
          // screen: a fresh tab, another device, or a pick a booking tool made mid-flow all leave
          // this browser holding no answer while the conversation carries one.
          const picked = typeof location_id === 'number' ? location_id : getLocationId();
          // The pre-chat form outranks the location question: when it is asked, it
          // carries the club question itself and the server sends no shortlist.
          const metadata =
            prechatCardMetadata(
              prechatFormRef.current,
              prechatFormRef.current?.satisfied ?? false
            ) ?? locationCardMetadata(locationScopeRef.current, picked);
          const card = metadata
            ? {
                id: `location_card_${session_id}`,
                content: salesStarterRef.current || askLocationPrompt(),
                senderType: 'assistant' as const,
                timestamp: convertedMessages[0]?.timestamp ?? new Date(),
                isStreaming: false,
                metadata,
              }
            : null;
          setMessageHistory(card ? [card, ...convertedMessages] : convertedMessages);
        }
      })
    );

    // Handle chat_settings - apply colors and starter message
    unsubscribers.push(
      onWebSocketEvent<ChatSettingsEvent>(ServerEventType.CHAT_SETTINGS, (event) => {
        const settings = event.data;
        setChatSettings(settings);
        onChatSettings?.(settings);
        const scope = settings.location_scope;
        locationScopeRef.current = scope ?? null;
        prechatFormRef.current = settings.prechat_form ?? null;
        // The server re-sends this after every pick, carrying the club the conversation actually
        // holds. It outranks anything this browser remembers: a change it refused (a locked
        // conversation, or a club that switched changing off) has to snap back rather than leave
        // a club on screen that was never accepted.
        const confirmed = typeof scope?.location_id === 'number' ? scope.location_id : null;
        if (confirmed !== null && confirmed !== getLocationId()) {
          saveLocationId(confirmed);
        }
        const pickedLocationId = confirmed ?? getLocationId();

        // Re-assert a pick the server may not know about yet, which is the case before the
        // conversation exists. Skipped once the server has echoed the same club back, so the
        // echo cannot bounce into another round trip.
        if (pickedLocationId && confirmed !== pickedLocationId) {
          websocket.send({
            type: ClientEventType.SELECT_LOCATION,
            location_id: pickedLocationId,
          });
        }

        const starterMessage = settings.sales_starter?.trim();
        salesStarterRef.current = starterMessage ?? '';
        // Unanswered this is the mandatory picker; answered it is the confirmation the visitor
        // can tap to move club.
        const locationCard = locationCardMetadata(scope, pickedLocationId);
        const openingCard = locationCard;
        const openingCallId = LOCATION_SCOPE_TOOL_CALL_ID;
        const openingPrompt = askLocationPrompt();


        // Re-assert an answer this browser holds that the server has not seen: a new
        // socket, a rotated session, or a conversation that has not been created yet all
        // leave the server saying 'not answered' about someone who answered yesterday.
        if (settings.prechat_form && !settings.prechat_form.satisfied) {
          const remembered = getContactDetails();
          if (remembered?.mobile_phone) {
            websocket.send({
              type: ClientEventType.SUBMIT_PRECHAT_FORM,
              details: remembered as unknown as Record<string, unknown>,
            });
          }
        }

        const prechatCard = prechatCardMetadata(
          settings.prechat_form,
          settings.prechat_form?.satisfied ?? false
        );

        if (currentMessages.length === 0 && (starterMessage || openingCard)) {
          // One bubble: the greeting carries the picker, so a visitor is not greeted twice.
          // Without a greeting configured, the question itself is the greeting.
          addBotMessage(
            `starter_${Date.now()}`,
            starterMessage || openingPrompt,
            openingCard
          );
          // The contact form is a bubble of its own, and it has to follow that greeting.
          // A club with a starter configured took this branch on a visitor's very first
          // settings event, and the branch only ever carried the club picker, so the form
          // never arrived at all: the composer stayed locked on
          // `prechat_form.satisfied === false` and the visitor had nothing to answer. It
          // only appeared to work because a second settings event (a club pick, or a
          // remembered answer being re-asserted) fell through to the branch below.
          // Appending is safe here for the same reason it is there: addBotMessage is a
          // functional update, so the write above cannot undo it.
          if (prechatCard) {
            addBotMessage(
              `prechat_${Date.now()}`,
              settings.prechat_form?.prompt || askContactPrompt(),
              prechatCard
            );
          }
        } else {
          // Both cards the widget mints are rebuilt by the server on every settings event, so
          // both are re-applied here: a club that moved, or contact details that have now been
          // accepted. One pass and one write, because two writes in the same tick would each
          // start from the same ref and the second would undo the first.
          const transcript = messagesRef.current;
          const patched = [...transcript];
          let changed = false;
          const reapply = (callId: string, next: Record<string, unknown> | undefined) => {
            const index = patched.findIndex(
              (message) =>
                (message.metadata?.tool_ui as { tool_call_id?: string } | undefined)
                  ?.tool_call_id === callId
            );
            if (index === -1) return false;
            const nextMetadata = { ...(next ?? {}) };
            if (JSON.stringify(patched[index].metadata ?? {}) !== JSON.stringify(nextMetadata)) {
              patched[index] = { ...patched[index], metadata: nextMetadata };
              changed = true;
            }
            return true;
          };

          reapply(openingCallId, openingCard);
          const prechatOnScreen = reapply(PRECHAT_FORM_TOOL_CALL_ID, prechatCard);
          if (changed) setMessageHistory(patched);

          // Appended after the write above, never before it: the write starts from a ref that
          // cannot know about a message added in the same tick, so adding the form first meant
          // the rewrite dropped it again the moment the club was picked, leaving the visitor
          // who had just answered the club question with nothing on screen. Appending is safe
          // in either order because it is a functional update.
          if (prechatCard && !prechatOnScreen) {
            addBotMessage(
              `prechat_${Date.now()}`,
              settings.prechat_form?.prompt || askContactPrompt(),
              prechatCard
            );
          }
        }
      })
    );

    // Handle streaming_chunk - append to streaming buffer
    unsubscribers.push(
      onWebSocketEvent<StreamingChunkEvent>(ServerEventType.STREAMING_CHUNK, (event) => {
        const { message_id, chunk, is_first } = event.data;
        setAssistantTyping(false);

        // If this is the first chunk, start a new streaming message
        if (is_first) {
          startStreamingMessage(message_id);
        }

        // Append the chunk to the streaming message
        appendStreamingChunk(message_id, chunk);
      })
    );

    // Handle stream_complete - finalize message
    unsubscribers.push(
      onWebSocketEvent<StreamCompleteEvent>(ServerEventType.STREAM_COMPLETE, (event) => {
        const { message_id, content, metadata } = event.data;
        setAssistantTyping(false);
        finalizeStreamingMessage(message_id, content, metadata);
      })
    );

    // Handle new_message - add non-streamed message
    unsubscribers.push(
      onWebSocketEvent<NewMessageEvent>(ServerEventType.NEW_MESSAGE, (event) => {
        const { message_id, content, sender_type, metadata } = event.data;

        // Don't add user messages - they're already added locally
        if (sender_type === 'user') {
          return;
        }

        setAssistantTyping(false);

        // Add bot or system message
        if (sender_type === 'assistant') {
          addBotMessage(message_id, content, metadata);
        } else if (sender_type === 'system') {
          addSystemMessage(content);
        }
      })
    );

    // Handle language_update - sync UI language
    unsubscribers.push(
      onWebSocketEvent<LanguageUpdateEvent>(ServerEventType.LANGUAGE_UPDATE, (event) => {
        const language = event.data.language?.trim();
        if (language) {
          onLanguageUpdate?.(language);
        }
      })
    );

    // Handle error - display error
    unsubscribers.push(
      onWebSocketEvent<ErrorEvent>(ServerEventType.ERROR, (event) => {
        const { message, code } = event.data;
        setAssistantTyping(false);

        // A session that can't be restored isn't a real error for the user.
        // session_ready already fired during connect and saved a fresh, valid
        // session id, so just swallow the banner and carry on.
        if (code && RESTORE_FAILURE_CODES.has(code)) {
          return;
        }

        setLastError(message);
        onError?.(message, code);
      })
    );

    // Handle whatsapp_handoff - show (or re-open) the banner
    unsubscribers.push(
      onWebSocketEvent<WhatsAppHandoffEvent>(ServerEventType.WHATSAPP_HANDOFF, (event) => {
        const { code, url, raise } = event.data;
        onWhatsAppHandoff?.({ code, url }, raise === true);
      })
    );

    // Cleanup: unsubscribe all handlers
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [
    onWebSocketEvent,
    addBotMessage,
    addSystemMessage,
    startStreamingMessage,
    appendStreamingChunk,
    finalizeStreamingMessage,
    setMessageHistory,
    setAssistantTyping,
    currentMessages.length,
    saveSession,
    onChatSettings,
    onLanguageUpdate,
    onError,
    onWhatsAppHandoff,
    convertServerMessages,
  ]);

  return {
    chatSettings,
    lastError,
    clearError,
  };
}

export default useChatEventHandlers;
